import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaSearch, 
  FaChartLine, 
  FaGraduationCap, 
  FaHandshake, 
  FaShieldAlt, 
  FaHeadset,
  FaDatabase,
  FaTools
} from 'react-icons/fa';

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services = [
    {
      id: 1,
      icon: <FaSearch />,
      title: "Broker Listings",
      description: "Our core service is providing detailed listings of regulated and registered brokers that offer instant withdrawals. Each broker is thoroughly vetted before being added to our platform.",
      features: [
        "Comprehensive broker database",
        "Detailed regulatory information",
        "Performance metrics and reviews"
      ]
    },
    {
      id: 2,
      icon: <FaChartLine />,
      title: "Broker Comparison",
      description: "We offer powerful comparison tools that allow you to evaluate brokers side-by-side based on key factors like regulation, fees, withdrawal speed, and available assets.",
      features: [
        "Side-by-side comparisons",
        "Customizable filters",
        "Performance analytics"
      ]
    },
    {
      id: 3,
      icon: <FaGraduationCap />,
      title: "Educational Resources",
      description: "Our platform provides educational materials about cryptocurrency trading, risk management, and how to choose the right broker for your needs.",
      features: [
        "Comprehensive guides",
        "Video tutorials",
        "Market analysis"
      ]
    },
    {
      id: 4,
      icon: <FaHandshake />,
      title: "Client Guidance",
      description: "We offer personalized guidance to help you navigate the process of selecting and connecting with the right broker for your trading goals.",
      features: [
        "One-on-one consultations",
        "Customized recommendations",
        "Ongoing support"
      ]
    },
    {
      id: 5,
      icon: <FaShieldAlt />,
      title: "Security Audits",
      description: "We conduct regular security audits of listed brokers to ensure they maintain the highest standards of fund protection and data security.",
      features: [
        "Regular security assessments",
        "Compliance verification",
        "Fraud prevention tools"
      ]
    },
    {
      id: 6,
      icon: <FaHeadset />,
      title: "24/7 Support",
      description: "Our dedicated support team is available around the clock to assist you with any questions or issues you may encounter.",
      features: [
        "Multi-channel support",
        "Expert assistance",
        "Fast response times"
      ]
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    visible: { 
      transition: { 
        staggerChildren: 0.1 
      } 
    }
  };

  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Comprehensive solutions for your trading needs
          </motion.p>
        </div>
      </div>
      
      <div className="container">
        <motion.div 
          className="services-intro"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeInUp}
          ref={ref}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p>
            UPM Traders provides a comprehensive suite of services designed to help traders find 
            the best regulated brokers with instant withdrawal capabilities for cryptocurrency trading. 
            Our expert team combines deep industry knowledge with cutting-edge technology to deliver 
            unparalleled value to our clients.
          </p>
        </motion.div>
        
        <motion.div 
          className="services-grid staggered-animations"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerChildren}
        >
          {services.map((service, index) => (
            <motion.div 
              className="service-card" 
              key={service.id}
              variants={fadeInUp}
              whileHover={{ 
                y: -10,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <motion.div 
                className="service-icon"
                animate={floatAnimation}
                whileHover={{ 
                  scale: 1.1,
                  backgroundColor: "#D4AF37",
                  color: "#121F38"
                }}
              >
                {service.icon}
              </motion.div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 5, color: "#D4AF37" }}
                  >
                    {feature}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div 
          className="services-cta"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeInUp}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>Ready to Get Started?</h2>
          <p>Join thousands of traders who have found their perfect broker through our platform</p>
          <motion.button 
            className="btn btn-brand"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Explore Our Services
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;